import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export default function MeasurementsPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ ph: "", chlorine: "", salt: "", note: "" });

  const load = () => api.get("/measurements").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ph: f.ph ? parseFloat(f.ph) : null,
      chlorine: f.chlorine ? parseFloat(f.chlorine) : null,
      salt: f.salt ? parseFloat(f.salt) : null,
      note: f.note,
    };
    await api.post("/measurements", payload);
    toast.success(t("add_measurement") + " ✓");
    setF({ ph: "", chlorine: "", salt: "", note: "" });
    load();
  };

  const data = items.map((m) => ({
    t: new Date(m.recorded_at).toLocaleDateString(),
    pH: m.ph,
    Cl: m.chlorine,
    Salt: m.salt,
  }));

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">{t("add_measurement")}</h3>
          <form className="grid gap-4 sm:grid-cols-4" onSubmit={submit}>
            <div>
              <Label>{t("ph")}</Label>
              <Input type="number" step="0.01" value={f.ph} onChange={(e) => setF({ ...f, ph: e.target.value })} data-testid="meas-ph-input" />
            </div>
            <div>
              <Label>{t("chlorine")}</Label>
              <Input type="number" step="0.01" value={f.chlorine} onChange={(e) => setF({ ...f, chlorine: e.target.value })} data-testid="meas-cl-input" />
            </div>
            <div>
              <Label>{t("salt")}</Label>
              <Input type="number" step="0.01" value={f.salt} onChange={(e) => setF({ ...f, salt: e.target.value })} data-testid="meas-salt-input" />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" data-testid="meas-submit-btn">
                {t("save")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">{t("trend")}</h3>
          {data.length === 0 ? (
            <div className="grid h-64 place-items-center text-sm text-muted-foreground">{t("no_data")}</div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Legend />
                  <Line type="monotone" dataKey="pH" stroke="#00A6FB" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Cl" stroke="#EF233C" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Salt" stroke="#87986A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
